import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    TextField,
    Autocomplete,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
    Card,
    CardContent,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Alert,
    useTheme,
    alpha,
    List,
    ListItem,
    ListItemText,
    Stack
} from '@mui/material';
import { BarChart } from '@mui/x-charts';
import {
    Search as SearchIcon,
    CompareArrows as CompareIcon,
    Inventory as PackageIcon,
    Person as UnitaryIcon,
    Info as InfoIcon,
    FilterList as FilterIcon,
    AttachMoney as MoneyIcon,
    AccountBalance as BankIcon,
} from '@mui/icons-material';
import axios from 'axios';
import flaskServer from '../config/flaskserver';

const ComparadorProcedimentos = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [modo, setModo] = useState('unitario');
    const [filtro, setFiltro] = useState('todos');
    const [resultados, setResultados] = useState([]);
    const [error, setError] = useState('');

    const token = localStorage.getItem('token');
    const authHeader = { headers: { 'Authorization': `Bearer ${token}` } };

    const handleSearch = async (query) => {
        if (query.length < 2) return;
        setSearching(true);
        try {
            const response = await axios.get(`${flaskServer.flaskServerIP}/comparador-procedimentos/sugestoes?q=${query}&tipo=${filtro}`, authHeader);
            setOptions(response.data);
        } catch (err) {
            console.error(err);
        } finally {
            setSearching(false);
        }
    };

    const fetchComparacao = async (item, currentModo) => {
        if (!item) return;
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(
                `${flaskServer.flaskServerIP}/comparador-procedimentos/comparar?codigo=${item.codigo}&modo=${currentModo}&tipo=${filtro}`,
                authHeader
            );
            if (response.data.status === 'success') {
                setResultados(response.data.resultados);
            } else if (response.data.status === 'empty') {
                setResultados([]);
                setError(response.data.message);
            }
        } catch (err) {
            setError('Erro ao buscar dados de comparação.');
            setResultados([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedItem) {
            fetchComparacao(selectedItem, modo);
        }
    }, [modo, filtro]);

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                <CompareIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                <Box>
                    <Typography variant="h4" fontWeight={800} gutterBottom sx={{ mb: 0 }}>
                        Comparador de Procedimentos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Analise custos unitários e pacotes históricos entre diferentes convênios.
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Search and Filters */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    fullWidth
                                    options={options}
                                    getOptionLabel={(option) => `${option.codigo} - ${option.descricao}`}
                                    loading={searching}
                                    onInputChange={(e, value) => handleSearch(value)}
                                    onChange={(e, value) => {
                                        setSelectedItem(value);
                                        fetchComparacao(value, modo);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar Procedimento, Material ou Medicamento"
                                            variant="outlined"
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {searching ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <ToggleButtonGroup
                                    value={modo}
                                    exclusive
                                    onChange={(e, next) => next && setModo(next)}
                                    size="small"
                                    color="primary"
                                >
                                    <ToggleButton value="unitario" sx={{ px: 3 }}>
                                        <UnitaryIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Unitário
                                    </ToggleButton>
                                    <ToggleButton value="pacote" sx={{ px: 3 }}>
                                        <PackageIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Pacote
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <FilterIcon sx={{ color: 'text.secondary', mr: 1, alignSelf: 'center' }} />
                            {['todos', 'procedimento', 'material', 'medicamento', 'taxa'].map((f) => (
                                <Chip
                                    key={f}
                                    label={f.charAt(0).toUpperCase() + f.slice(1)}
                                    onClick={() => setFiltro(f)}
                                    color={filtro === f ? 'primary' : 'default'}
                                    variant={filtro === f ? 'filled' : 'outlined'}
                                    sx={{ borderRadius: 1 }}
                                />
                            ))}
                        </Box>
                    </Paper>
                </Grid>

                {error && (
                    <Grid item xs={12}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>{error}</Alert>
                    </Grid>
                )}

                {/* Dashboard Results */}
                <Grid item xs={12}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {resultados.length > 0 ? (
                                <>
                                    <Grid item xs={12}>
                                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                                            Comparação de Resultados: {selectedItem?.codigo}
                                        </Typography>
                                    </Grid>

                                    {/* Gráfico de Ranking */}
                                    <Grid item xs={12}>
                                        <Paper sx={{ p: 3, borderRadius: 4, mb: 1, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                                            <Typography variant="subtitle1" fontWeight={700} gutterBottom display="flex" alignItems="center">
                                                <MoneyIcon sx={{ color: 'success.main', mr: 1 }} />
                                                Ranking de Distorção Geográfica (Maior para o Menor Valor)
                                            </Typography>
                                            <Box sx={{ height: 350, width: '100%', mt: 2 }}>
                                                <BarChart
                                                    layout="horizontal"
                                                    yAxis={[{
                                                        scaleType: 'band',
                                                        data: [...resultados].sort((a, b) => b.valor - a.valor).map(r => r.convenio)
                                                    }]}
                                                    series={[{
                                                        data: [...resultados].sort((a, b) => b.valor - a.valor).map(r => r.valor),
                                                        color: theme.palette.success.light,
                                                        label: 'Valor de Remuneração (R$)'
                                                    }]}
                                                    height={350}
                                                    margin={{ left: 150 }}
                                                    tooltip={{ trigger: 'item' }}
                                                />
                                            </Box>
                                        </Paper>
                                    </Grid>

                                    {/* Cards de Detalhamento */}
                                    {resultados.sort((a, b) => b.valor - a.valor).map((res, index) => (
                                        <Grid item xs={12} md={4} key={index}>
                                            <Card sx={{
                                                height: '100%',
                                                borderRadius: 3,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.03)',
                                                transition: 'transform 0.2s',
                                                '&:hover': { transform: 'translateY(-5px)' }
                                            }}>
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                        <Typography variant="overline" color="text.secondary" fontWeight={800}>
                                                            {res.convenio}
                                                        </Typography>
                                                        <Chip
                                                            label={modo === 'unitario' ? 'Unitário' : 'Pacote'}
                                                            size="small"
                                                            color={modo === 'unitario' ? 'info' : 'secondary'}
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                    <Typography variant="h4" fontWeight={900} color="primary.main">
                                                        R$ {res.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                    </Typography>

                                                    {modo === 'pacote' && (
                                                        <Box sx={{ mt: 3 }}>
                                                            <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <InfoIcon fontSize="small" color="action" />
                                                                Associações Frequentes
                                                            </Typography>
                                                            <Divider sx={{ mb: 1.5 }} />
                                                            <List size="small" disablePadding>
                                                                {res.associacoes.map((assoc, idx) => (
                                                                    <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                                                                        <ListItemText
                                                                            primary={assoc.descricao}
                                                                            secondary={`${assoc.codigo} | Freq: ${assoc.frequencia}x`}
                                                                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true }}
                                                                            secondaryTypographyProps={{ variant: 'caption' }}
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                                {res.associacoes.length === 0 && (
                                                                    <Typography variant="caption" color="text.secondary italic">
                                                                        Nenhuma associação detectada.
                                                                    </Typography>
                                                                )}
                                                            </List>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                                                Baseado em {res.num_guias_analisadas} guias faturadas.
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {modo === 'unitario' && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                                            {res.descricao}
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </>
                            ) : selectedItem && (
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 4, bgcolor: 'background.default' }}>
                                        <Typography variant="h6" color="text.secondary">
                                            Selecione um item para ver a comparação.
                                        </Typography>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Container>
    );
};

export default ComparadorProcedimentos;
